import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null); // null = checking, false = anon, obj = authed
    const [loading, setLoading] = useState(true);

    const refresh = useCallback(async () => {
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
        } catch {
            setUser(false);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { refresh(); }, [refresh]);

    const login = useCallback(async (email, password) => {
        const { data } = await api.post("/auth/login", { email, password });
        if (data.token) localStorage.setItem("outpost_token", data.token);
        setUser(data.user);
        return data.user;
    }, []);

    const register = useCallback(async (name, email, password) => {
        const { data } = await api.post("/auth/register", { name, email, password });
        if (data.token) localStorage.setItem("outpost_token", data.token);
        setUser(data.user);
        return data.user;
    }, []);

    const logout = useCallback(async () => {
        try {
            await api.post("/auth/logout");
        } catch (e) {
            console.warn("Logout request failed (clearing local session anyway):", e?.message || e);
        }
        localStorage.removeItem("outpost_token");
        setUser(false);
    }, []);

    const isAdmin = !!user && ["network_owner", "network_admin", "server_admin", "admin"].includes(user.role);

    const value = useMemo(
        () => ({ user, loading, login, register, logout, refresh, setUser, isAdmin }),
        [user, loading, login, register, logout, refresh, isAdmin]
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
